import type { ReactNode } from 'react';
import { Card } from './Card.js';
import { Icon } from './Icon.js';
import { PageHeader } from './Page.js';

/** Uma tela sugerida: para onde o link leva e o nome dela. */
export interface AddressSuggestion {
  to: string;
  label: string;
}

/**
 * O endereço que não existe — UMA anatomia para toda tela de não encontrado,
 * nas duas aplicações.
 *
 * Até 26/10/2026 eram três desenhos: a interna com o cartão de sugestões quando
 * o endereço apontava um módulo (`/erp/…`), a mesma interna só com o botão
 * quando não apontava, e o portal com um `EmptyState` centralizado. A do ERP
 * era a que respondia melhor — diz o endereço pedido e oferece para onde ir —,
 * e virou a de todas.
 *
 * Serve aos dois casos de "não existe": o endereço que nenhuma rota casa
 * (título padrão) e o registro que a ficha pediu e a API não achou — aí o
 * título diz o que faltou ("Este veículo não existe no cadastro desta base") e
 * `hint` diz o porquê provável. A ficha não desenha a própria moldura em volta:
 * a tela inteira é esta, igual em todas.
 *
 * O conteúdo, e não a tela: quem chama abre o `<Page>` em volta, como toda
 * tela abre (régua `tela-sem-page`).
 *
 * Esta camada não conhece o roteador de nenhuma das duas aplicações: quem chama
 * desenha o link (`renderLink`) e os botões (`actions`), sempre com navegação
 * do roteador, e nunca com recarga.
 */
export function AddressNotFound({
  title = 'Página não encontrada',
  hint,
  path,
  suggestions,
  renderLink,
  actions,
}: {
  /** O que não foi achado, dito como fato. Sem ele, "Página não encontrada". */
  title?: string;
  /** O porquê provável e o que fazer, depois do endereço. Sem ele, a frase padrão. */
  hint?: ReactNode;
  /** O endereço que a pessoa pediu, mostrado como foi pedido. */
  path: string;
  /** As telas para onde ir. Sem nenhuma, o cartão não aparece. */
  suggestions?: { title: string; description: string; items: AddressSuggestion[] };
  renderLink: (link: { to: string; className: string; children: ReactNode }) => ReactNode;
  actions: ReactNode;
}) {
  const items = suggestions?.items ?? [];

  return (
    <>
      <PageHeader title={title}>
        Procuramos em{' '}
        <code className="rounded-pequeno border border-borda-suave bg-superficie px-1.5 py-0.5 font-mono text-[0.85em] break-all text-texto">
          {path}
        </code>{' '}
        e não achamos nada.{' '}
        {hint ??
          (items.length > 0
            ? 'Confira o endereço, ou siga por um dos caminhos abaixo.'
            : 'Confira o endereço, ou volte ao início.')}
      </PageHeader>

      {suggestions && items.length > 0 && (
        <Card title={suggestions.title} descricao={suggestions.description} bleed>
          <ul className="divide-y divide-borda-suave">
            {items.map((item) => (
              <li key={item.to}>
                {renderLink({
                  to: item.to,
                  className:
                    'flex min-h-11 items-center justify-between gap-3 px-4 py-2.5 text-sm font-medium transition-colors hover:bg-superficie-suave focus-visible:bg-superficie-suave',
                  children: (
                    <>
                      {item.label}
                      <Icon nome="seta-direita" size={16} className="text-texto-suave" />
                    </>
                  ),
                })}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex flex-wrap gap-3">{actions}</div>
    </>
  );
}
